export type HistoricalGranularity = 'Day' | 'Week' | 'Month'

export interface HistoricalDataPoint {
  period: string
  dials: number
  conversations: number
  creditPulls: number
  applications: number
  submissions: number
  locks: number
  pullToApp: number
  appToSub: number
  subToSign: number
  signToLock: number
  pipelineVolume: number
  fundingsVolume: number
}

export interface HistoricalSummary {
  metric: string
  average: number
  best: number
  bestPeriod: string
  worst: number
  worstPeriod: string
  trend: 'up' | 'down' | 'neutral'
}

// Base daily benchmarks
const DAILY_BENCHMARKS = {
  dials: 40,
  conversations: 25,
  creditPulls: 15,
  applications: 10,
  submissions: 6,
  locks: 4,
}

const generateDataPoints = (
  granularity: HistoricalGranularity,
): HistoricalDataPoint[] => {
  const points: HistoricalDataPoint[] = []
  const count = granularity === 'Day' ? 30 : 12
  const multiplier = granularity === 'Day' ? 1 : granularity === 'Week' ? 5 : 22

  for (let i = 0; i < count; i++) {
    let period = ''
    if (granularity === 'Day') {
      const d = new Date()
      d.setDate(d.getDate() - (count - i - 1))
      period = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    } else if (granularity === 'Week') {
      period = `Week ${i + 1}`
    } else {
      const d = new Date()
      d.setMonth(d.getMonth() - (count - i - 1))
      period = d.toLocaleDateString('en-US', { month: 'short' })
    }

    // Add some random variance around the benchmarks
    const variance = () => 0.7 + Math.random() * 0.6 // 70% to 130% of benchmark

    points.push({
      period,
      dials: Math.round(DAILY_BENCHMARKS.dials * multiplier * variance()),
      conversations: Math.round(
        DAILY_BENCHMARKS.conversations * multiplier * variance(),
      ),
      creditPulls: Math.round(
        DAILY_BENCHMARKS.creditPulls * multiplier * variance(),
      ),
      applications: Math.round(
        DAILY_BENCHMARKS.applications * multiplier * variance(),
      ),
      submissions: Math.round(
        DAILY_BENCHMARKS.submissions * multiplier * variance(),
      ),
      locks: Math.round(DAILY_BENCHMARKS.locks * multiplier * variance()),
      pullToApp: Math.round(60 + Math.random() * 20), // 60-80%
      appToSub: Math.round(55 + Math.random() * 25), // 55-80%
      subToSign: Math.round(70 + Math.random() * 20), // 70-90%
      signToLock: Math.round(60 + Math.random() * 25), // 60-85%
      pipelineVolume: Math.round(
        (3000000 + Math.random() * 2000000) * (multiplier / 5),
      ),
      fundingsVolume: Math.round(
        (1500000 + Math.random() * 1000000) * (multiplier / 5),
      ),
    })
  }
  return points
}

const calculateSummary = (
  data: HistoricalDataPoint[],
  metricKey: keyof HistoricalDataPoint,
  metricName: string,
): HistoricalSummary => {
  let sum = 0
  let best = -Infinity
  let worst = Infinity
  let bestPeriod = ''
  let worstPeriod = ''

  data.forEach((point) => {
    const val = point[metricKey] as number
    sum += val
    if (val > best) {
      best = val
      bestPeriod = point.period
    }
    if (val < worst) {
      worst = val
      worstPeriod = point.period
    }
  })

  const average = Math.round(sum / data.length)

  // Simple trend: compare last 3 periods to first 3 periods
  const firstAvg =
    (((((data[0][metricKey] as number) + data[1][metricKey]) as number) +
      data[2][metricKey]) as number) / 3
  const lastAvg =
    (((((data[data.length - 1][metricKey] as number) +
      data[data.length - 2][metricKey]) as number) +
      data[data.length - 3][metricKey]) as number) / 3

  let trend: 'up' | 'down' | 'neutral' = 'neutral'
  if (lastAvg > firstAvg * 1.05) trend = 'up'
  else if (lastAvg < firstAvg * 0.95) trend = 'down'

  return {
    metric: metricName,
    average,
    best,
    bestPeriod,
    worst,
    worstPeriod,
    trend,
  }
}

export const getHistoricalData = (granularity: HistoricalGranularity) => {
  const dataPoints = generateDataPoints(granularity)
  const multiplier = granularity === 'Day' ? 1 : granularity === 'Week' ? 5 : 22

  const benchmarks = {
    dials: DAILY_BENCHMARKS.dials * multiplier,
    conversations: DAILY_BENCHMARKS.conversations * multiplier,
    creditPulls: DAILY_BENCHMARKS.creditPulls * multiplier,
    applications: DAILY_BENCHMARKS.applications * multiplier,
    submissions: DAILY_BENCHMARKS.submissions * multiplier,
    locks: DAILY_BENCHMARKS.locks * multiplier,
  }

  const summaries: HistoricalSummary[] = [
    calculateSummary(dataPoints, 'dials', 'Dials'),
    calculateSummary(dataPoints, 'conversations', 'Conversations'),
    calculateSummary(dataPoints, 'creditPulls', 'Credit Pulls'),
    calculateSummary(dataPoints, 'applications', 'Applications'),
    calculateSummary(dataPoints, 'submissions', 'Submissions'),
    calculateSummary(dataPoints, 'locks', 'Locks'),
  ]

  return { dataPoints, summaries, benchmarks }
}
